import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

/**
 * Der geteilte Frontend-Unterbau: Axios-Instanz, Auth-Interceptor, Refresh-Warteschlange.
 *
 * **Warum das ein Paket ist und keine Datei je App:** caterinx hat zwei React-Anwendungen —
 * `apps/web` (Lieferantenportal) und `apps/admin` (D11). Zwei Kopien dieses Interceptors sind
 * der Fehler, der die getrennte Admin-App teuer macht: Ein Refresh-Fix wandert dann in eine
 * Kopie, und die andere loggt Nutzer weiter grundlos aus.
 *
 * Gegenüber Atleos `apps/web/src/lib/api.ts` ist die Logik unverändert. Neu ist nur, dass die
 * drei Dinge, die zwischen den Apps **legitim** verschieden sind, Parameter geworden sind statt
 * Konstanten: Basis-URL, Speicher-Präfix und das Ziel beim Zwangs-Logout. Alles andere — der
 * `_retry`-Schutz, die Warteschlange gegen parallele Refreshes, das Ausklammern der
 * Auth-Endpunkte selbst — ist eins zu eins übernommen.
 */

export interface AuthClientConfig {
  /** Basis-URL der REST-API, z. B. `https://api-staging.caterinx.com`. */
  baseUrl: string;
  /**
   * Präfix der localStorage-Schlüssel, z. B. `caterinx` → `caterinx-auth-token`.
   * Beide Apps laufen auf eigenen Origins, teilen sich den Speicher also ohnehin nicht;
   * der Präfix hält trotzdem die Entwicklungsumgebung (beide auf `localhost`) sauber.
   */
  storagePrefix: string;
  /**
   * Wohin nach einem endgültig gescheiterten Refresh umgeleitet wird.
   * `apps/web` und `apps/admin` haben unterschiedliche Login-Routen.
   */
  loginPath: string;
}

export interface AuthClient {
  /** Die vorkonfigurierte Axios-Instanz. Alle API-Aufrufe laufen hierüber. */
  readonly http: AxiosInstance;
  readonly config: AuthClientConfig;
  getToken(): string | null;
  getRefreshToken(): string | null;
  setToken(token: string | null): void;
  setRefreshToken(token: string | null): void;
  /** Holt ein neues ID-Token. Gibt `null` zurück, wenn der Refresh scheitert. */
  refreshAuthToken(): Promise<string | null>;
  /** Verwirft beide Token und leitet auf `loginPath` um. */
  forceLogout(): void;
  /** Name des `window`-Events, das nach einem erfolgreichen Refresh gefeuert wird. */
  readonly tokenRefreshedEvent: string;
}

/** Nutzlast des Refresh-Events — der `AuthProvider` hält seinen State darüber synchron. */
export interface TokenRefreshedDetail {
  token: string;
}

export function createAuthClient(config: AuthClientConfig): AuthClient {
  if (!config.baseUrl) {
    // Lauter Fehler in der Entwicklung statt stiller 404er gegen die eigene Origin.
    throw new Error('createAuthClient: baseUrl fehlt (VITE_API_URL nicht gesetzt?)');
  }

  const AUTH_STORAGE_KEY = `${config.storagePrefix}-auth-token`;
  const REFRESH_TOKEN_KEY = `${config.storagePrefix}-refresh-token`;
  const TOKEN_REFRESHED_EVENT = `${config.storagePrefix}-token-refreshed`;

  const http = axios.create({ baseURL: config.baseUrl });

  // Zustand der Refresh-Warteschlange: verhindert parallele Refresh-Aufrufe, wenn mehrere
  // Requests gleichzeitig in einen 401 laufen.
  let isRefreshing = false;
  let refreshSubscribers: ((token: string) => void)[] = [];

  const onRefreshed = (newToken: string): void => {
    refreshSubscribers.forEach((callback) => callback(newToken));
    refreshSubscribers = [];
  };

  const readStorage = (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      // Privater Modus oder blockierter Speicher — wie "nicht angemeldet" behandeln.
      return null;
    }
  };

  const writeStorage = (key: string, value: string | null): void => {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      // Nicht persistierbar: die Sitzung lebt dann nur bis zum Reload. Kein Grund abzubrechen.
    }
  };

  const forceLogout = (): void => {
    writeStorage(AUTH_STORAGE_KEY, null);
    writeStorage(REFRESH_TOKEN_KEY, null);
    window.location.href = config.loginPath;
  };

  const refreshAuthToken = async (): Promise<string | null> => {
    const refreshToken = readStorage(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;

    try {
      // Direkter axios-Aufruf, nicht über `http` — sonst läuft der Refresh in den eigenen
      // Response-Interceptor und damit in eine Schleife.
      const response = await axios.post<{ token: string }>(`${config.baseUrl}/auth/refresh`, { refreshToken });
      const newToken = response.data.token;
      writeStorage(AUTH_STORAGE_KEY, newToken);
      window.dispatchEvent(
        new CustomEvent<TokenRefreshedDetail>(TOKEN_REFRESHED_EVENT, { detail: { token: newToken } })
      );
      return newToken;
    } catch {
      return null;
    }
  };

  http.interceptors.request.use(
    (requestConfig) => {
      const token = readStorage(AUTH_STORAGE_KEY);
      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
      return requestConfig;
    },
    (error) => Promise.reject(error)
  );

  http.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

      // Nur bei 401, nur einmal je Request, und nie für die Auth-Endpunkte selbst.
      if (
        !originalRequest ||
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login')
      ) {
        return Promise.reject(error);
      }

      if (!readStorage(REFRESH_TOKEN_KEY)) {
        forceLogout();
        return Promise.reject(error);
      }

      // Läuft schon ein Refresh, wird dieser Request eingereiht statt einen zweiten zu starten.
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(http(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAuthToken();

        if (!newToken) {
          refreshSubscribers = [];
          forceLogout();
          return Promise.reject(error);
        }

        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return http(originalRequest);
      } catch (refreshError) {
        refreshSubscribers = [];
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return {
    http,
    config,
    tokenRefreshedEvent: TOKEN_REFRESHED_EVENT,
    getToken: () => readStorage(AUTH_STORAGE_KEY),
    getRefreshToken: () => readStorage(REFRESH_TOKEN_KEY),
    setToken: (token) => writeStorage(AUTH_STORAGE_KEY, token),
    setRefreshToken: (token) => writeStorage(REFRESH_TOKEN_KEY, token),
    refreshAuthToken,
    forceLogout,
  };
}
